import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../utils/api'
import { useAuth } from '../hooks/useAuth'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

interface OptOutRequest {
  id: string
  broker_name: string
  broker_domain: string
  status: 'pending' | 'sent' | 'confirmed' | 'failed' | 'expired'
  data_types_found: string[]
  created_at: string
  sent_at: string | null
  confirmed_at: string | null
  deadline_at: string | null
  days_remaining: number | null
}

interface OptOutSummary {
  total_requests: number
  pending: number
  sent: number
  confirmed: number
  failed: number
  expired: number
  brokers_cleaned: number
  brokers_remaining: number
}

interface DashboardData {
  summary: OptOutSummary
  requests: OptOutRequest[]
  can_initiate_optouts: boolean
  has_paid_report: boolean
}

interface BrokerForOptOut {
  name: string
  domain: string
  email: string
  data_types: string[]
  already_requested: boolean
}

interface PartnerReferral {
  name: string
  type: string
  description: string
  url: string
  logo_url: string | null
  is_partner: boolean
}

export default function OptOutDashboard() {
  const { user } = useAuth()
  const [dashboard, setDashboard] = useState<DashboardData | null>(null)
  const [availableBrokers, setAvailableBrokers] = useState<BrokerForOptOut[]>([])
  const [partners, setPartners] = useState<PartnerReferral[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedBrokers, setSelectedBrokers] = useState<Set<string>>(new Set())
  const [initiating, setInitiating] = useState(false)
  const [showBrokerList, setShowBrokerList] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  async function fetchData() {
    setLoading(true)
    try {
      const [dashboardRes, brokersRes, partnersRes] = await Promise.all([
        api.get('/optouts/dashboard'),
        api.get('/optouts/available-brokers').catch(() => ({ data: { brokers: [] } })),
        api.get('/partners/referrals'),
      ])
      setDashboard(dashboardRes.data)
      setAvailableBrokers(brokersRes.data.brokers || [])
      setPartners(partnersRes.data)
    } catch (err) {
      console.error('Failed to load opt-out dashboard:', err)
    } finally {
      setLoading(false)
    }
  }

  async function handleBulkOptOut() {
    if (selectedBrokers.size === 0) return

    setInitiating(true)
    try {
      const response = await api.post('/optouts/bulk', {
        broker_ids: Array.from(selectedBrokers),
        request_name: user?.email?.split('@')[0] || 'User',
        request_email: user?.email,
        request_state: null,
      })

      setMessage({
        type: 'success',
        text: `Successfully initiated ${response.data.initiated} opt-out requests!`
      })
      setSelectedBrokers(new Set())
      setShowBrokerList(false)
      fetchData()
    } catch (err) {
      setMessage({
        type: 'error',
        text: 'Failed to initiate opt-out requests. Please try again.'
      })
    } finally {
      setInitiating(false)
    }
  }

  async function handleMarkConfirmed(optoutId: string) {
    try {
      await api.patch(`/optouts/${optoutId}/status`, null, {
        params: { new_status: 'confirmed' }
      })
      fetchData()
    } catch (err) {
      console.error('Failed to update status:', err)
    }
  }

  function toggleBrokerSelection(domain: string) {
    const newSelected = new Set(selectedBrokers)
    if (newSelected.has(domain)) {
      newSelected.delete(domain)
    } else {
      newSelected.add(domain)
    }
    setSelectedBrokers(newSelected)
  }

  function selectAllBrokers() {
    const notRequested = availableBrokers.filter(b => !b.already_requested)
    setSelectedBrokers(new Set(notRequested.map(b => b.domain)))
  }

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    sent: 'bg-blue-500/20 text-blue-400',
    confirmed: 'bg-green-500/20 text-green-400',
    failed: 'bg-red-500/20 text-red-400',
    expired: 'bg-dark-600 text-dark-400',
  }

  const statusLabels: Record<string, string> = {
    pending: 'Queued',
    sent: 'Request Sent',
    confirmed: 'Removed',
    failed: 'Failed',
    expired: 'No Response',
  }

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-24 pb-16 px-4 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ginger-500"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <Navbar />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 text-sm text-dark-400 mb-2">
              <Link to="/dashboard" className="hover:text-white">Dashboard</Link>
              <span>/</span>
              <span className="text-white">Opt-Out Tracker</span>
            </div>
            <h1 className="text-3xl font-bold mb-2">Data Broker Opt-Outs</h1>
            <p className="text-dark-400">
              Track and manage your data removal requests. Exercise your privacy rights under CCPA/GDPR.
            </p>
          </div>

          {/* Messages */}
          {message && (
            <div className={`mb-6 p-4 rounded-lg border ${
              message.type === 'success'
                ? 'bg-green-500/20 border-green-500/50 text-green-400'
                : 'bg-red-500/20 border-red-500/50 text-red-400'
            }`}>
              {message.text}
              <button
                onClick={() => setMessage(null)}
                className="float-right text-sm opacity-60 hover:opacity-100"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Summary Cards */}
          {dashboard && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="card text-center">
                <div className="text-3xl font-bold text-ginger-500">
                  {dashboard.summary.confirmed}
                </div>
                <div className="text-dark-400 text-sm">Brokers Cleaned</div>
              </div>
              <div className="card text-center">
                <div className="text-3xl font-bold text-blue-400">
                  {dashboard.summary.sent + dashboard.summary.pending}
                </div>
                <div className="text-dark-400 text-sm">In Progress</div>
              </div>
              <div className="card text-center">
                <div className="text-3xl font-bold text-yellow-400">
                  {availableBrokers.filter(b => !b.already_requested).length}
                </div>
                <div className="text-dark-400 text-sm">Brokers to Clean</div>
              </div>
              <div className="card text-center">
                <div className="text-3xl font-bold text-dark-400">
                  {dashboard.summary.total_requests}
                </div>
                <div className="text-dark-400 text-sm">Total Requests</div>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Content - Opt-Out Requests */}
            <div className="lg:col-span-2 space-y-6">
              {/* Action Bar */}
              <div className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-bold text-lg">Start Removing Your Data</h2>
                    <p className="text-dark-400 text-sm">
                      Select brokers and we'll help you send opt-out requests
                    </p>
                  </div>
                  {dashboard?.has_paid_report ? (
                    <button
                      onClick={() => setShowBrokerList(!showBrokerList)}
                      className="btn btn-primary"
                    >
                      {showBrokerList ? 'Hide Brokers' : 'Select Brokers'}
                    </button>
                  ) : (
                    <Link to="/#pricing" className="btn btn-primary">
                      Get Full Report First
                    </Link>
                  )}
                </div>

                {/* Broker Selection Panel */}
                {showBrokerList && availableBrokers.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-dark-700">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-dark-400">
                        {selectedBrokers.size} selected
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={selectAllBrokers}
                          className="text-sm text-ginger-500 hover:underline"
                        >
                          Select All
                        </button>
                        <button
                          onClick={() => setSelectedBrokers(new Set())}
                          className="text-sm text-dark-400 hover:text-white"
                        >
                          Clear
                        </button>
                      </div>
                    </div>

                    <div className="max-h-64 overflow-y-auto space-y-2 mb-4">
                      {availableBrokers.map((broker) => (
                        <label
                          key={broker.domain}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                            broker.already_requested
                              ? 'bg-dark-800 opacity-50 cursor-not-allowed'
                              : selectedBrokers.has(broker.domain)
                              ? 'bg-ginger-500/20 border border-ginger-500'
                              : 'bg-dark-800 hover:bg-dark-700'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedBrokers.has(broker.domain)}
                            onChange={() => toggleBrokerSelection(broker.domain)}
                            disabled={broker.already_requested}
                            className="w-4 h-4 rounded border-dark-600 text-ginger-500 focus:ring-ginger-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{broker.name}</div>
                            <div className="text-xs text-dark-400 truncate">
                              {broker.data_types.slice(0, 3).join(', ')}
                              {broker.data_types.length > 3 && ` +${broker.data_types.length - 3} more`}
                            </div>
                          </div>
                          {broker.already_requested && (
                            <span className="text-xs bg-dark-600 px-2 py-1 rounded">Requested</span>
                          )}
                        </label>
                      ))}
                    </div>

                    {selectedBrokers.size > 0 && (
                      <button
                        onClick={handleBulkOptOut}
                        disabled={initiating}
                        className="btn btn-primary w-full"
                      >
                        {initiating ? (
                          <span className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                            Initiating...
                          </span>
                        ) : (
                          `Start ${selectedBrokers.size} Opt-Out Requests`
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Active Requests */}
              <div className="card">
                <h2 className="font-bold text-lg mb-4">Your Opt-Out Requests</h2>

                {!dashboard || dashboard.requests.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-4">📋</div>
                    <h3 className="font-bold mb-2">No opt-out requests yet</h3>
                    <p className="text-dark-400 mb-4">
                      Start by selecting brokers above to remove your data.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dashboard.requests.map((request) => (
                      <div
                        key={request.id}
                        className="bg-dark-800 rounded-lg p-4 flex items-center justify-between"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{request.broker_name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${statusColors[request.status]}`}>
                              {statusLabels[request.status]}
                            </span>
                          </div>
                          <div className="text-xs text-dark-400 mt-1">
                            {request.status === 'sent' && request.days_remaining !== null && (
                              <span>
                                Legal deadline: {request.days_remaining} days remaining
                              </span>
                            )}
                            {request.status === 'confirmed' && request.confirmed_at && (
                              <span>
                                Confirmed {new Date(request.confirmed_at).toLocaleDateString()}
                              </span>
                            )}
                            {request.status === 'pending' && (
                              <span>
                                Queued on {new Date(request.created_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>

                        {request.status === 'sent' && (
                          <button
                            onClick={() => handleMarkConfirmed(request.id)}
                            className="text-xs text-ginger-500 hover:underline"
                          >
                            Mark Confirmed
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* How It Works */}
              <div className="card">
                <h3 className="font-bold mb-4">How Opt-Outs Work</h3>
                <div className="space-y-4 text-sm">
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-ginger-500/20 text-ginger-500 flex items-center justify-center text-xs font-bold">
                      1
                    </div>
                    <div>
                      <div className="font-medium">Select Brokers</div>
                      <div className="text-dark-400">Choose which data brokers to target</div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-ginger-500/20 text-ginger-500 flex items-center justify-center text-xs font-bold">
                      2
                    </div>
                    <div>
                      <div className="font-medium">We Send Requests</div>
                      <div className="text-dark-400">CCPA/GDPR compliant deletion requests</div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-ginger-500/20 text-ginger-500 flex items-center justify-center text-xs font-bold">
                      3
                    </div>
                    <div>
                      <div className="font-medium">Track Progress</div>
                      <div className="text-dark-400">Brokers have 45 days to comply</div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-xs font-bold">
                      ✓
                    </div>
                    <div>
                      <div className="font-medium">Data Removed</div>
                      <div className="text-dark-400">Your info is deleted from their systems</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Partner Referrals */}
              <div className="card">
                <h3 className="font-bold mb-2">Need More Help?</h3>
                <p className="text-dark-400 text-sm mb-4">
                  Our trusted partners can handle your privacy needs professionally.
                </p>

                <div className="space-y-3">
                  {partners.map((partner) => (
                    <a
                      key={partner.name}
                      href={partner.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block bg-dark-800 rounded-lg p-4 hover:bg-dark-700 transition-colors"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{partner.name}</span>
                        <span className="text-xs bg-ginger-500/20 text-ginger-400 px-2 py-0.5 rounded">
                          Partner
                        </span>
                      </div>
                      <p className="text-dark-400 text-xs">
                        {partner.description}
                      </p>
                      <div className="text-ginger-500 text-xs mt-2 flex items-center gap-1">
                        Learn More
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </div>
                    </a>
                  ))}
                </div>

                <div className="mt-4 p-3 bg-dark-800 rounded-lg text-xs text-dark-400">
                  <strong className="text-white">Why partners?</strong> For comprehensive protection,
                  professional services can monitor for re-listings and handle complex removal cases.
                </div>
              </div>

              {/* Requirements Checklist */}
              <div className="card">
                <h3 className="font-bold mb-2">Security Checklist</h3>
                <p className="text-dark-400 text-sm mb-4">
                  Based on your exposure level, here's what we recommend:
                </p>

                <div className="space-y-2 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="rounded border-dark-600 text-ginger-500" />
                    <span>Data broker opt-outs (in progress)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="rounded border-dark-600 text-ginger-500" />
                    <span>Password manager setup</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="rounded border-dark-600 text-ginger-500" />
                    <span>Credit freeze at all bureaus</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="rounded border-dark-600 text-ginger-500" />
                    <span>Two-factor authentication enabled</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="rounded border-dark-600 text-ginger-500" />
                    <span>Breach passwords changed</span>
                  </label>
                </div>

                <a
                  href="mailto:services@monadnockcyber.ai?subject=Security%20Consultation"
                  className="btn btn-secondary w-full mt-4 text-center block"
                >
                  Get Professional Assessment
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}
